import React, { useState } from 'react';
import classNames from 'classnames';
import { Avatar, Icon, Overlay, ScrollStopper } from '@gumtree/ui-library';
import { Slide } from '@gumtree/ui-library/src/transitions';
import { useOnClickOutside } from '@gumtree/ui-library/src/hooks/use-on-click-outside';
import { Account, AccountSelection } from '@gumtree/shared/src/types/account';
import { UsersData } from '../../reducers/common/user-data';
import {
    StyledWrapper,
    StyledButton,
    StyledAccountMenuWrapper,
    StyledMobileMenuContent,
    StyledProfileSection,
    StyledAvatar,
    StyledUserName,
    StyledAccountsList,
    StyledAccountItem,
    StyledAccountName,
    StyledCheckIcon,
} from './account-menu.style';

type Props = {
    mobile?: boolean;
    userData: UsersData;
    account: Account;
    accountSelections: AccountSelection[];
    onChangeAccount: (accountId: string) => void;
};

const AccountMenu: React.FC<Props> = ({
    mobile = false,
    userData,
    account,
    accountSelections,
    onChangeAccount,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const desktopRef = React.useRef<HTMLDivElement>(null);
    const mobileRef = React.useRef<HTMLDivElement>(null);

    useOnClickOutside(desktopRef, () => setIsOpen(false));

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const closeMenu = () => {
        setIsOpen(false);
    };

    const handleAccountClick = (accountId: string) => {
        onChangeAccount(accountId);
        setIsOpen(false);
    };

    const menuContent = (
        <>
            <StyledProfileSection>
                <StyledAvatar>
                    <Avatar name={`${userData.firstName}`} />
                </StyledAvatar>
                <StyledUserName>{userData.firstName}</StyledUserName>
            </StyledProfileSection>

            <StyledAccountsList>
                {accountSelections.length <= 1 ? (
                    <StyledAccountName>Pro account {account.id}</StyledAccountName>
                ) : (
                    accountSelections.map((section, index) => (
                        <StyledAccountItem
                            key={section.value}
                            isSelected={index === 0}
                            onClick={() => handleAccountClick(section.value)}
                        >
                            <StyledAccountName>{section.name}</StyledAccountName>
                            <StyledCheckIcon isSelected={index === 0}>
                                <Icon type="check" color="white" />
                            </StyledCheckIcon>
                        </StyledAccountItem>
                    ))
                )}
            </StyledAccountsList>
        </>
    );

    if (mobile) {
        return (
            <StyledWrapper mobile={mobile} className="account-menu">
                <StyledButton onClick={toggleMenu}>
                    <Avatar name={`${userData.firstName} ${userData.lastName}`} />
                </StyledButton>
                {isOpen && <ScrollStopper dialogRoot={mobileRef} />}
                <Overlay isOpen={isOpen} onClick={closeMenu} />
                <Slide in={isOpen} direction="left">
                    <StyledMobileMenuContent ref={mobileRef}>{menuContent}</StyledMobileMenuContent>
                </Slide>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper
            ref={desktopRef}
            mobile={mobile}
            className={classNames('account-menu', { opened: isOpen })}
        >
            <StyledButton onClick={toggleMenu}>
                <Avatar name={`${userData.firstName} ${userData.lastName}`} />
            </StyledButton>
            <StyledAccountMenuWrapper isOpen={isOpen}>{menuContent}</StyledAccountMenuWrapper>
        </StyledWrapper>
    );
};

export default AccountMenu;
